import {
  DomElementAlignment,
  DomTargetPosition,
  IconButton,
  iconMap,
  OdinIcon,
  OdinIconSize,
  Tooltip,
  useTestTag,
  ResizableHandle,
  ResizablePanel,
} from '@myosh/odin-components';
import type { DialogRef } from '@myosh/odin-components';
import clsx from 'clsx';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useReducer, useRef } from 'react';
import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { BehaviorSubject, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { usePopState } from '../../hooks/use-pop-state';
import { useAppDispatch } from '../../redux/hooks';
import { setActivePanelEmpty, setActivePanelFilled, setActiveRecordMinimised } from '../../redux/slices/layout';
import { invalidateRecordSpecificTags, invalidateSharedTags } from '../../utils/invalidate-cache-tags-utils';
import ConfirmationDialog from '../common/confirmation-dialog.component';
import ActiveRecordPanel from './active-record-panel.component';
import type { RecordPanelInfo } from './active-record-panel.component';
import reducer, { ActiveRecordActionType } from './active-record.reducer';
import type { ActiveRecordChildSetting } from './active-record.reducer';
import useActiveRecordBatchSave from './hooks/use-active-record-batch-save';
import { useActiveRecordLayout } from './hooks/use-active-record-layout';

const MIN_RESIZABLE_PANEL_WIDTH = 40;

export interface ActiveRecordChildRenderProps {
  onCloseActiveRecord: (id: string) => void;
  settingsSubject: BehaviorSubject<ActiveRecordContextProps>;
  /**
   * Used to pass settings through the navigator to use when adding an item to the navigator.
   */
  extraSettings?: unknown;
  onCloseActiveRecordConfirmationModalRef: DialogRef | null;
}

interface ActiveRecordProps {
  isCollapsed: boolean;
  children: (props: ActiveRecordChildRenderProps) => ReactNode | ReactNode[] | React.JSX.Element;
  useConfirmationOnClose?: boolean;
  onClose: () => void;
  onActiveRecordChanged: (childSetting: ActiveRecordChildSetting) => void;
  onLastActiveRecordClosed: () => void;
  onFullScreenChanged: (fullScreen: boolean) => void;
  fullScreen?: boolean;
  hidden?: boolean;
}

export interface ActiveRecordContextProps {
  fullScreen: boolean;
  isNavCollapsed: boolean;
  batchSaveInProgress?: boolean;
  numberOfDirtyItems?: number;
  numberOfFailedItems?: number;
}

export interface ActiveRecordRef {
  /**
   * Adds (or navigates to) an active record .
   *
   * @param {string} recordKey - The active record key.
   * @param {string} newIcon - An optional icon to set
   * @param {string | JSX.Element} details - Optional details to set
   * @param {boolean} fullscreen - Sets the item in full screen mode.
   * @param extraSettings - Optional extra settings
   */
  addActiveRecord: (
    recordKey: string,
    icon: string,
    details: string | React.JSX.Element,
    fullscreen?: boolean,
    extraSettings?: unknown
  ) => void;
  /**
   * Updates an existing active record.
   *
   * @param {string} recordKey - The existing (old) active record key.
   * @param {string} newRecordKey - The new record key to set for the active record.
   * @param {string} newIcon - An optional new icon to set
   * @param {string | JSX.Element} newDetails - Optional new details to set
   * @param {boolean} newFullScreen - Sets the item in full screen mode.
   * @param newExtraSettings - Optional new extra settings
   * @param {boolean} forceRender - Force a render of the active record item by changing the existing renderKey
   */
  updateActiveRecord: (
    recordKey: string,
    newRecordKey: string,
    newIcon?: string,
    newDetails?: string | React.JSX.Element,
    newFullScreen?: boolean,
    newExtraSettings?: unknown,
    forceRender?: boolean
  ) => void;
  /**
   * @returns The number of items in the active record panel.
   */
  currentRecordCount: () => number;
  /**
   * Display an overlay over the active record panel and disable field editing.
   *
   * @param {boolean} frozen - Set the value to disable/enable the active record panel.
   */
  setActiveRecordPanelFrozen: (frozen: boolean) => void;
  setItemDirty: (id: string) => void;
  clearItemDirty: (id: string) => void;
  setItemSaveFailed: (id: string) => void;
  /** Returns whether the active record panel is currently displayed in full screen mode. */
  isFullScreen: () => boolean;
}

function ActiveRecord(
  {
    isCollapsed,
    children,
    useConfirmationOnClose = true, // always use confirmation on close
    onClose,
    onActiveRecordChanged,
    onLastActiveRecordClosed,
    onFullScreenChanged,
    fullScreen = false,
    hidden = false,
  }: ActiveRecordProps,
  ref: Ref<ActiveRecordRef>
) {
  const dispatch = useAppDispatch();
  const tagCreator = useTestTag('active-record');
  const { t } = useTranslation();
  const { isMobile } = useActiveRecordLayout();

  const [state, dispatcher] = useReducer(reducer, {
    displayCollapsed: isCollapsed,
    childSettings: [],
    currentFullScreen: fullScreen,
    minimiseActiveRecord: true,
    frozen: false,
  });

  const settingsSubject = useRef(
    new BehaviorSubject<ActiveRecordContextProps>({
      fullScreen,
      isNavCollapsed: true,
    })
  );
  const onCloseActiveRecordConfirmationModalRef = useRef<DialogRef>(null);
  const childKeysRef = useRef<Array<string>>([]);
  const destroySubject = useRef(new Subject<void>());

  const { setItemDirty, clearItemDirty, setItemSaveFailed, hasDirtyItems, showBatchSaveInProgress } =
    useActiveRecordBatchSave(settingsSubject.current, setActiveRecordPanelFrozen, closeAll);

  useEffect(() => {
    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  // Reset fullscreen state on browser back/forward navigation
  // This is needed because window.history.replaceState bypasses React Router,
  // causing state to persist incorrectly after back navigation
  const handlePopState = useCallback(() => {
    dispatcher({ type: ActiveRecordActionType.SetFullScreen, currentFullScreen: false });
  }, []);

  usePopState(handlePopState);

  useEffect(() => {
    if (state.childSettings.length === 0) {
      dispatch(setActivePanelEmpty());
    } else {
      dispatch(setActivePanelFilled());
    }
  }, [state.childSettings.length, dispatch]);

  useEffect(() => {
    if (state.activeRecordSetting) {
      onActiveRecordChanged?.(state.activeRecordSetting);
    }
  }, [state.activeRecordSetting]);

  useEffect(() => {
    dispatch(setActiveRecordMinimised(!state.minimiseActiveRecord));
  }, [state.minimiseActiveRecord, dispatch]);

  // Once we bring the active record panels into this component then this will be used to eliminate the need for the context
  const onRecordPanelChanged = (info: RecordPanelInfo) => {
    if (info.fullScreen !== undefined) {
      dispatcher({
        type: ActiveRecordActionType.SetFullScreen,
        currentFullScreen: info.fullScreen,
        displayCollapsed: state.displayCollapsed,
      });

      settingsSubject.current.next({
        ...settingsSubject.current.value,
        fullScreen: info.fullScreen,
        isNavCollapsed: state.displayCollapsed,
      });

      onFullScreenChanged(info.fullScreen);
    }
  };

  const addActiveRecord = (
    recordKey: string,
    icon: string,
    details: string | React.JSX.Element,
    fullscreen?: boolean,
    extraSettings?: unknown
  ) => {
    const isFullScreenMode = fullscreen || false;

    settingsSubject.current.next({
      ...settingsSubject.current.value,
      fullScreen: isFullScreenMode,
      isNavCollapsed: state.displayCollapsed,
    });

    dispatcher({
      type: ActiveRecordActionType.AddActiveRecord, //adds or "navigates to" item if already present
      activeRecordSetting: {
        key: recordKey,
        renderKey: v4(),
        icon,
        details,
        extraSettings,
      },
      currentFullScreen: isFullScreenMode,
    });

    if (useConfirmationOnClose) {
      childKeysRef.current = [...childKeysRef.current, recordKey];
    }

    onFullScreenChanged(isFullScreenMode);
  };

  const closeActiveRecord = (recordKey: string) => {
    if (state.childSettings.length === 1) {
      onLastActiveRecordClosed?.();
    }

    dispatcher({
      type: ActiveRecordActionType.CloseActiveRecord,
      recordKey,
    });

    if (useConfirmationOnClose) {
      childKeysRef.current = childKeysRef.current.filter((keys) => keys !== recordKey);
    }
  };

  const updateActiveRecord = (
    recordKey: string,
    newRecordKey: string,
    newIcon?: string,
    newDetails?: string | React.JSX.Element,
    newFullScreen?: boolean,
    newExtraSettings?: unknown,
    forceRender?: boolean
  ) => {
    dispatcher({
      type: ActiveRecordActionType.UpdateActiveRecord,
      recordKey: recordKey,
      newRecordSetting: {
        key: newRecordKey,
        renderKey: forceRender ? v4() : undefined, // when set, explicitly triggers the render of this item
        icon: newIcon,
        details: newDetails,
        extraSettings: newExtraSettings,
      },
      currentFullScreen: newFullScreen || false,
    });

    if (useConfirmationOnClose) {
      const index = childKeysRef.current?.indexOf(recordKey);
      if (index !== -1) {
        childKeysRef.current[index] = newRecordKey;
      }
    }
  };

  function setActiveRecordPanelFrozen(frozen: boolean) {
    dispatcher({ type: ActiveRecordActionType.SetActiveRecordPanelFrozen, frozen });
  }

  const currentRecordCount = () => {
    return state.childSettings.length;
  };

  const isFullScreen = () => {
    return state.currentFullScreen;
  };

  useImperativeHandle(ref, () => ({
    addActiveRecord,
    updateActiveRecord,
    currentRecordCount,
    setActiveRecordPanelFrozen,
    setItemDirty,
    clearItemDirty,
    setItemSaveFailed,
    isFullScreen,
  }));

  const onActiveRecordMinimised = (recordKey: string) => {
    if (!state.minimiseActiveRecord) {
      return true;
    } else if (recordKey === state.activeRecordSetting?.key) {
      return false;
    }

    return true;
  };

  const onRecordClicked = (recordKey: string) => {
    dispatcher({
      type: ActiveRecordActionType.SetActiveRecord,
      recordKey,
      minimiseActiveRecord: onActiveRecordMinimised(recordKey),
    });
  };

  function closeAll(invalidateCache: boolean = true) {
    if (hasDirtyItems() && invalidateCache) {
      const listOfRecordIds = state.childSettings
        .filter((record) => !record.key.includes('new'))
        .map((record) => record.key);
      listOfRecordIds.forEach((id) => {
        invalidateRecordSpecificTags(id);
      });
      invalidateSharedTags();
    }
    dispatcher({
      type: ActiveRecordActionType.CloseAllRecords,
    });
    onClose();
    if (useConfirmationOnClose) {
      childKeysRef.current = [];
    }
  }

  const handleModalClose = (returnValue?: string) => {
    if (returnValue !== 'save') {
      // User discarded - close with smart cache invalidation
      closeAll(false);
    }
  };

  const onCloseAllClick = () => {
    if (useConfirmationOnClose && hasDirtyItems()) {
      if (!settingsSubject.current.value.batchSaveInProgress) {
        onCloseActiveRecordConfirmationModalRef.current?.show();
      }
    } else {
      closeAll();
    }
  };

  const renderActiveRecordsNav = () => {
    return state.childSettings.map((childSetting, index) => {
      // record-navigator is used for Feature Tours
      const recordStyles = clsx('cursor-pointer h-10 border-b border-gray-2 record-navigator', {
        'bg-primary-2 text-mono-1': state.activeRecordSetting?.key === childSetting.key,
        'border-t mt-2': index === 0,
      });

      const displayText = (childSetting.extraSettings as Record<string, unknown>)['displayText'] as string;
      const recordInfo =
        !displayText || displayText === 'n/a' ? (
          childSetting.details
        ) : (
          <p className="truncate" title={displayText}>
            {displayText}
          </p>
        );
      const RecordInfo = () => <div className="min-w-0 flex-1 truncate text-xs">{recordInfo}</div>;

      return (
        <li
          key={`panel_item_${childSetting.key}`}
          className={recordStyles}
          onClick={() => onRecordClicked(childSetting.key)}
        >
          <a className="flex items-center justify-start" ref={tagCreator(`nav-item-${index}`)}>
            <Tooltip
              arrow={true}
              description={<RecordInfo />}
              tooltipAlignment={DomElementAlignment.TopRight}
              tooltipTargetPosition={DomTargetPosition.Left}
              tooltipClassName={'h-10 flex justify-start items-center text-mono-0'}
              disabled={!state.displayCollapsed}
            >
              <OdinIcon
                className="text-inherit m-3 shrink"
                size={OdinIconSize.ExtraSmall}
                icon={iconMap[childSetting.icon] || 'ClipboardList'}
              />
            </Tooltip>
            {!state.displayCollapsed && (
              <>
                <RecordInfo />
                <IconButton
                  icon="X"
                  iconSize={OdinIconSize.ExtraSmall}
                  iconClassName="relative -left-2.5"
                  classNames="mx-1"
                  onClick={(event) => {
                    closeActiveRecord(childSetting.key);
                    event.stopPropagation();
                  }}
                />
              </>
            )}
          </a>
        </li>
      );
    });
  };

  const renderActiveRecords = () => {
    return state.childSettings.map((childSetting) => {
      const panelCloseActiveRecord = (id: string) => {
        closeActiveRecord(childSetting.key);
        clearItemDirty(id);
      };
      const active = childSetting.key === state.activeRecordSetting?.key;

      return (
        <ActiveRecordPanel
          key={`panel_${childSetting.renderKey}`}
          opened={state.minimiseActiveRecord && active}
          fullScreen={state.currentFullScreen}
          onPanelInfoChanged={onRecordPanelChanged}
          hidden={hidden}
        >
          {children({
            onCloseActiveRecord: panelCloseActiveRecord,
            settingsSubject: settingsSubject.current,
            extraSettings: childSetting.extraSettings,
            onCloseActiveRecordConfirmationModalRef: onCloseActiveRecordConfirmationModalRef.current,
          })}
        </ActiveRecordPanel>
      );
    });
  };

  const navigationStyles = clsx('flex flex-col h-full bg-gray-4 transition-all shadow-left-2 flex-shrink-0', {
    'w-10': state.displayCollapsed,
    'w-80': !state.displayCollapsed,
    'opacity-50 pointer-events-none': state.frozen,
    hidden: hidden,
  });

  // expand-navigator is used for Feature Tours
  const expandButtonStyles = clsx(
    'absolute w-4 top-4 -left-4 border-2 border-transparent !bg-gray-4 rounded-l shadow-left-2 focus:outline-none transition-all expand-navigator'
  );

  const canRenderActiveRecords =
    state.childSettings.length > 0 &&
    (!useConfirmationOnClose || (useConfirmationOnClose && onCloseActiveRecordConfirmationModalRef.current));

  const resizableClasses = clsx('h-full', {
    hidden: hidden || !state.minimiseActiveRecord,
    'opacity-50 pointer-events-none state-frozen': state.frozen,
  });

  return (
    <>
      {canRenderActiveRecords ? (
        <>
          <ResizableHandle className={resizableClasses} />
          <ResizablePanel
            id="active-record-panel"
            order={2}
            defaultSize={MIN_RESIZABLE_PANEL_WIDTH}
            minSize={MIN_RESIZABLE_PANEL_WIDTH}
            className={resizableClasses}
          >
            {renderActiveRecords()}
          </ResizablePanel>
          <div className={navigationStyles}>
            {!isMobile && (
              <div className="relative" data-testid="expand-navigator">
                <IconButton
                  icon={state.displayCollapsed ? 'ChevronLeft' : 'ChevronRight'}
                  iconSize={OdinIconSize.Small}
                  iconClassName="relative -left-1.5"
                  classNames={expandButtonStyles}
                  onClick={() => {
                    dispatcher({
                      type: ActiveRecordActionType.SetDisplayCollapsed,
                      displayCollapsed: !state.displayCollapsed,
                    });
                  }}
                />
              </div>
            )}
            <div
              className="close-all-button cursor-pointer whitespace-nowrap px-2 pb-2 pt-5 text-right text-sm leading-7 text-primary-2" //close-all-button is used for Feature Tours
              onClick={onCloseAllClick}
              ref={tagCreator('close-all-icon')}
            >
              {state.displayCollapsed ? (
                <Tooltip
                  description={t('close-all')}
                  debounceTime={200}
                  tooltipClassName="bg-mono-1"
                  tooltipAlignment={DomElementAlignment.TopRight}
                >
                  <OdinIcon size={OdinIconSize.Medium} icon="X" className="relative -left-1 w-8" />
                </Tooltip>
              ) : (
                <span className="underline">Close All</span>
              )}
            </div>
            <ul className="custom-scroll grow list-none overflow-y-auto">{renderActiveRecordsNav()}</ul>
          </div>
        </>
      ) : null}
      {useConfirmationOnClose && (
        <ConfirmationDialog
          ref={onCloseActiveRecordConfirmationModalRef}
          onConfirm={showBatchSaveInProgress}
          onClose={handleModalClose}
          cancelButtonText={t('discard')}
          hasCancelButton
        />
      )}
    </>
  );
}

export default forwardRef(ActiveRecord);
